import { schemaForTemplate } from './screen-schemas/index';
import type { ScreenSchema } from './screen-schemas/types';
import { isIteratedSlot } from './screen-schemas/types';
import hotspotGameSchema from './screen-schemas/screen-hotspot-game.schema';
import {
  buildSlotText,
  createScreenAudioTextResolver,
  readPlaceholderValues,
  type NarratableScreen,
  type ResolveTextFn
} from './screenAudioText';
import {
  synthTemplatesChildId,
  withInheritedPlaceholderValues
} from './useScreenAudio';

export const hsgIntroMarkerKey = (hostId: string) => `${hostId}_intro`;
export const hsgLevelMarkerKey = (hostId: string, levelIndex: number) =>
  `${hostId}-level-${levelIndex}`;
export const hsgResultMarkerKey = (hostId: string) => `${hostId}_result`;

const hasTemplates = (row: Record<string, unknown>): boolean =>
  Array.isArray(row.templates) && (row.templates as unknown[]).length > 0;

/**
 * Narration text for one HSG intro/result row — flat fields or nested `templates[]`
 * (e.g. cards inside intro/result wrappers).
 */
export function buildHsgSectionRowText(
  row: Record<string, unknown>,
  resolveText: ResolveTextFn,
  options?: {
    fill?: Record<string, string | number>;
    /** When the row is one `intro[]` item, use `${hostId}.intro.${index}` translation keys. */
    introKeyPath?: { hostId: string; index: number };
  }
): string {
  const fill = options?.fill ?? readPlaceholderValues(row);

  if (hasTemplates(row)) {
    return buildHsgTemplatesChainText(row, resolveText, fill);
  }

  return buildHsgFlatRowText(row, resolveText, fill, options?.introKeyPath);
}

function buildHsgTemplatesChainText(
  row: Record<string, unknown>,
  resolveText: ResolveTextFn,
  fill: Record<string, string | number>
): string {
  const templates = row.templates as NarratableScreen[] | undefined;
  if (!templates?.length) return '';

  const parentRecord = row;
  const resolveWithFill = (k: string, v: string | number) =>
    resolveText(k, v, fill) ?? '';

  const parts: string[] = [];

  for (let i = 0; i < templates.length; i++) {
    const raw = templates[i] as NarratableScreen;
    const childWithId = (
      raw.id
        ? raw
        : { ...raw, id: synthTemplatesChildId(String(row.id ?? 'hsg'), i) }
    ) as NarratableScreen;
    const child = withInheritedPlaceholderValues(
      childWithId as Record<string, unknown>,
      parentRecord
    ) as NarratableScreen;

    const schema = schemaForTemplate(child.template as string | undefined);
    if (!schema) continue;

    const introSlot = schema.slots['intro'];
    if (!introSlot || isIteratedSlot(introSlot)) continue;

    const text = buildSlotText(
      introSlot,
      child as unknown as Record<string, unknown>,
      resolveWithFill
    );
    if (text.trim()) parts.push(text);
  }

  return parts.join('\n\n').trim();
}

function buildHsgFlatRowText(
  row: Record<string, unknown>,
  resolveText: ResolveTextFn,
  fill: Record<string, string | number>,
  introKeyPath?: { hostId: string; index: number }
): string {
  const resolveWithFill = (k: string, v: string | number) =>
    resolveText(k, v, fill) ?? '';

  const template = row.template as string | undefined;
  const schema =
    schemaForTemplate(template) ?? schemaForTemplate('content') ?? null;
  if (!schema?.slots['intro'] || isIteratedSlot(schema.slots['intro'])) {
    return '';
  }

  const introSlot = schema.slots['intro'];
  const screenForText: Record<string, unknown> = template
    ? row
    : introKeyPath
      ? { ...row, id: `${introKeyPath.hostId}.intro.${introKeyPath.index}` }
      : row.id
        ? row
        : { ...row, template: 'content' };

  return buildSlotText(introSlot, screenForText, resolveWithFill);
}

/** Combined intro narration for all `intro[]` rows (single `_intro` marker). */
export function buildHsgIntroSectionText(
  host: NarratableScreen,
  resolveText: ResolveTextFn
): string {
  const intro = host.intro as Record<string, unknown>[] | undefined;
  if (!Array.isArray(intro) || !host.id) return '';

  const fill = readPlaceholderValues(host as Record<string, unknown>);
  const parts: string[] = [];

  intro.forEach((raw, index) => {
    const text = buildHsgSectionRowText(raw, resolveText, {
      fill,
      introKeyPath: { hostId: host.id!, index }
    });
    if (text.trim()) parts.push(text);
  });

  return parts.join('\n\n').trim();
}

/** Level slot text using host schema (`level-0`, …). */
export function buildHsgLevelText(
  host: NarratableScreen,
  levelIndex: number,
  resolveText: ResolveTextFn
): string {
  if (!host.id) return '';
  const fill = readPlaceholderValues(host as Record<string, unknown>);
  const api = createScreenAudioTextResolver({
    screen: host,
    schema: hotspotGameSchema,
    resolveText: (k, v, values) => resolveText(k, v, values ?? fill)
  });
  return api.getSlotText(`level-${levelIndex}`);
}

export function hsgResultRowForAudio(
  row: Record<string, unknown>,
  hostId: string,
  scorePercent: number
): NarratableScreen {
  const scoreThreshold = row.scoreThreshold as string | undefined;
  const index = row.index as number | undefined;
  const translationId =
    (row.id as string | undefined) ??
    (scoreThreshold !== undefined
      ? `${hostId}.result.${scoreThreshold}.${index ?? 0}`
      : undefined);

  return {
    ...(row as NarratableScreen),
    id: hsgResultMarkerKey(hostId),
    ...(translationId ? { 'translation-id': translationId } : {}),
    template: (row.template as string | undefined) ?? 'content',
    'placeholder-values': {
      ...(readPlaceholderValues(row) as Record<string, string | number>),
      scorePercent
    }
  };
}

export function buildHsgResultText(
  row: Record<string, unknown>,
  hostId: string,
  scorePercent: number,
  resolveText: ResolveTextFn
): string {
  const fill: Record<string, string | number> = {
    ...readPlaceholderValues(row),
    scorePercent
  };
  return buildHsgSectionRowText(row, resolveText, { fill });
}
